// src/Medicalcase/components/Specialtyfilter/Specialtyfilter.jsx
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { getSpecialtyLabel } from "./specialtyLabel";
import "./Specialtyfilter.css";

const IFilter = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="lucide lucide-funnel-icon lucide-funnel"
  >
    <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
  </svg>
);

export default function SpecialtyFilter({ specialties = [], activeSpecialty, onChange }) {
  const { t, i18n } = useTranslation();
  const chipsRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Direction du texte selon la langue active (ar => rtl)
  const isRtl = i18n.dir(i18n.language) === "rtl";
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  const searchLabel = t("caseDiscussions.searchSpecialty", {
    defaultValue: "Search specialty",
  });

  // On garde la VALEUR (anglais, utilisée pour le filtre API)
  // et on calcule le LIBELLÉ traduit pour l'affichage + la recherche.
  const items = useMemo(
    () =>
      specialties.map((value) => ({
        value,
        label: getSpecialtyLabel(t, value),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specialties, t, i18n.language]
  );

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase(i18n.language);
    if (!normalizedSearch) return items;
    return items.filter(
      ({ value, label }) =>
        label.toLocaleLowerCase(i18n.language).includes(normalizedSearch) ||
        // on permet aussi de chercher avec le terme anglais d'origine
        String(value).toLocaleLowerCase().includes(normalizedSearch)
    );
  }, [searchTerm, items, i18n.language]);

  // direction = -1 (précédent) ou +1 (suivant).
  // En RTL, scrollLeft est inversé : on multiplie donc par -1.
  const slide = (direction) => {
    const el = chipsRef.current;
    if (!el) return;
    const rtlFactor = isRtl ? -1 : 1;
    el.scrollBy({
      left: direction * rtlFactor * Math.max(el.clientWidth * 0.75, 180),
      behavior: "smooth",
    });
  };

  return (
    <div className="mcd-filter">
      <div className="mcd-filter__toolbar">
        <div className="mcd-filter__label">
          <IFilter />
          <span>
            {t("caseDiscussions.filterBySpecialty", {
              defaultValue: "Filter by Specialty",
            })}
          </span>
        </div>
        <label className="mcd-filter__search">
          <Search size={16} aria-hidden="true" />
          <span className="sr-only">{searchLabel}</span>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={searchLabel}
            aria-label={searchLabel}
          />
        </label>
      </div>

      <div className="mcd-filter__carousel">
        <button
          type="button"
          className="mcd-filter__slide-button"
          onClick={() => slide(-1)}
          aria-label={t("caseDiscussions.previousSpecialties", {
            defaultValue: "Previous specialties",
          })}
          title={t("caseDiscussions.previousSpecialties", {
            defaultValue: "Previous specialties",
          })}
        >
          <PrevIcon size={18} aria-hidden="true" />
        </button>

        <div className="mcd-filter__chips" ref={chipsRef}>
          {filteredItems.length === 0 ? (
            <span className="mcd-filter__empty">
              {t("caseDiscussions.noSpecialtyFound", {
                defaultValue: "No specialty found",
              })}
            </span>
          ) : (
            filteredItems.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={activeSpecialty === value}
                className={`mcd-filter__chip ${
                  activeSpecialty === value ? "mcd-filter__chip--active" : ""
                }`}
                onClick={() => onChange?.(value)}
              >
                {label}
              </button>
            ))
          )}
        </div>

        <button
          type="button"
          className="mcd-filter__slide-button"
          onClick={() => slide(1)}
          aria-label={t("caseDiscussions.nextSpecialties", {
            defaultValue: "Next specialties",
          })}
          title={t("caseDiscussions.nextSpecialties", {
            defaultValue: "Next specialties",
          })}
        >
          <NextIcon size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}